<template>
  <VConfirmation
    v-model="actionModel"
    :heading="$tc('substore.resendDocument_headline', optionsCnt)"
    v-bind="$attrs"
    :is-busy="loading"
    class="row"
    closable
    @expand="onUpdateAction"
  >
    <template #content="{ name }">
      <ul class="bc-table no-spacing">
        <li v-for="(option, index) in options" :key="index" class="bc-row">
          <div class="bc-cell">
            <VButton v-focus="index === 0" :label="buttonLabel(option)" class="btn-small btn-brand" @click="onAction(option)" />
          </div>
          <div v-if="option.lastSentAt" class="bc-cell last-send-at">
            {{ $t(`substore.${name}_lastSentAt`, option) }}
          </div>
        </li>
      </ul>
      <div v-if="optionsCnt" class="offset-top-1 bc-table-persistent">
        <div class="notification-left">
          <div class="icon-frame">
            <VSvgIcon name="information" size="large" />
          </div>
        </div>
        <div class="bc-cell">
          {{ $tm(`substore.${name}_info_`, options[0].type, true, optionsCnt, 'tc') }}
        </div>
      </div>
    </template>
  </VConfirmation>
</template>

<script>
import VConfirmation from '@/components/Confirmation';
import VButton from '@/components/Button';
import Proxy from '@/proxies/SubstoreStockProxy';
import { formatDate } from '@/utils/date';
import { getLegacyErrorMessage } from '@/utils/i18n';
import focus from '@/directives/focus';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';

export default {
  name: 'ButtonChoice',

  directives: {
    focus,
  },

  components: {
    VConfirmation,
    VButton,
    VSvgIcon,
  },

  inheritAttrs: false,

  props: {
    action: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      options: null,
      loading: true,
    };
  },

  computed: {
    actionModel: {
      get() {
        return this.action;
      },
      set(v) {
        this.$emit('update:action', v);
      },
    },

    optionsCnt() {
      return this.options?.length || 0;
    },
  },

  watch: {
    action: 'onUpdateAction',
  },

  methods: {
    async onUpdateAction() {
      this.loading = false;
      this.options = null;

      if (!this.action) return;
      if (this.action.message) return;

      this.loading = true;

      try {
        await this.$store.dispatch('contract/change', { uniqueSubscriptionId: this.action.uniqueSubscriptionId });
        const r = await new Proxy().getDocuments(this.action.subscriptionTemplateId, this.action.uniqueSubscriptionId);
        this.options = r.data.documents;
      } catch (e) {
        this.actionModel = Object.assign({}, this.action, {
          message: {
            type: 'ERROR',
            supplier: () => getLegacyErrorMessage(e),
          },
        });
      }

      this.options = this.options?.map((option) => ({
        ...option,
        lastSentAt: formatDate(option.lastSentAt),
      }));
      this.loading = false;
    },

    onClose() {
      this.$emit('update:action');
    },

    async onAction(option) {
      if (!this.action) return;
      this.loading = true;
      const { name, subscriptionTemplateId, uniqueSubscriptionId } = this.action;
      try {
        const r = await new Proxy().documentsResend(subscriptionTemplateId, uniqueSubscriptionId, option.type, option.event);
        this.actionModel = Object.assign({}, this.action, {
          message: {
            type: 'SUCCESS',
            supplier: () => this.$t(`substore.${name}_success_${r.data.recipientType}${r.data.crmIntendedPurpose ? '_with_purpose' : ''}`, r.data),
          },
        });
      } catch (e) {
        this.actionModel = Object.assign({}, this.action, {
          message: {
            type: 'ERROR',
            supplier: () => getLegacyErrorMessage(e),
            heading: 'substore.failed',
          },
        });
      }
    },

    buttonLabel(option) {
      return option.buttonLabel ?? this.$t('substore.resendDocument_btnDefault');
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.last-send-at {
  padding-left: 12px;
  margin-bottom: 24px;
  color: $font-color-light;
}

@mixin cell {
  .bc-cell {
    display: table-cell;
    text-align: left;
    vertical-align: middle;
  }
}

.no-spacing {
  padding: 0;
  margin: 0;
}

@media screen and (min-width: #{$grid-medium-min + 1}px) {
  .bc-table {
    display: table;
    border-spacing: 0 12px;
    border-collapse: separate;

    .bc-row {
      display: table-row;
      @include cell();
    }
  }
}

@media screen and (max-width: #{$grid-medium-min}px) {
  ::v-deep .notification-dismissible {
    padding-right: $building-unit;

    > .close {
      margin-right: 0;
      margin-left: -$building-unit;
    }
  }
}

.bc-table-persistent {
  display: table;
  @include cell();
}
</style>
